import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";

function Dot({ on, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-6 w-6"
      aria-label={label}
    >
      <span
        className={[
          "absolute inset-0 rounded-full border transition-colors duration-300",
          on ? "border-gray-900" : "border-gray-300",
        ].join(" ")}
      />
      <span
        className={[
          "absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-300",
          on ? "bg-gray-900" : "bg-gray-300",
        ].join(" ")}
      />
    </button>
  );
}

export default function InsightsCarousel({ INSIGHTS, Heading, BtnText, onOpenDetail }) {
  const [active, setActive] = useState(0);

  const scrollerRef = useRef(null);
  const slideRefs = useRef([]);

  const activeRef = useRef(0);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const programmaticRef = useRef(false);
  const programmaticTimer = useRef(null);

  const scrollToIndex = (i) => {
    const el = slideRefs.current?.[i];
    if (!el) return;

    programmaticRef.current = true;
    clearTimeout(programmaticTimer.current);

    setActive(i);

    el.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });

    programmaticTimer.current = setTimeout(() => {
      programmaticRef.current = false;
    }, 350);
  };

  const goTo = (i) => scrollToIndex(i);
  const goNext = () => scrollToIndex((activeRef.current + 1) % INSIGHTS.length);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let raf = 0;

    const onScroll = () => {
      if (programmaticRef.current) return;

      cancelAnimationFrame(raf);

      raf = requestAnimationFrame(() => {
        const items = slideRefs.current.filter(Boolean);
        if (!items.length) return;

        const left = scroller.getBoundingClientRect().left;

        let best = 0;
        let bestDist = Infinity;

        items.forEach((node, idx) => {
          const dist = Math.abs(node.getBoundingClientRect().left - left);
          if (dist < bestDist) {
            bestDist = dist;
            best = idx;
          }
        });

        if (best !== activeRef.current) {
          setActive(best);
        }
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
      clearTimeout(programmaticTimer.current);
    };
  }, []);

  const isDown = useRef(false);
  const startX = useRef(0);
  const startScrollLeft = useRef(0);
  const moved = useRef(false);
  const pointerIdRef = useRef(null);

  const onPointerDown = (e) => {
    // On touch devices, let the browser handle vertical scrolling naturally.
    if (e.pointerType && e.pointerType !== "mouse") return;

    // Ignore drag when clicking buttons or links
    if (e.target.closest("button, a")) return;

    const scroller = scrollerRef.current;
    if (!scroller) return;

    isDown.current = true;
    moved.current = false;
    pointerIdRef.current = e.pointerId;

    startX.current = e.clientX;
    startScrollLeft.current = scroller.scrollLeft;

    scroller.setPointerCapture(e.pointerId);
    scroller.style.cursor = "grabbing";
  };
  const onPointerMove = (e) => {
    const scroller = scrollerRef.current;
    if (!scroller || !isDown.current) return;

    const dx = e.clientX - startX.current;

    if (Math.abs(dx) > 12) moved.current = true;

    scroller.scrollLeft = startScrollLeft.current - dx;
  };

  const endDrag = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    if (pointerIdRef.current !== null) {
      try {
        scroller.releasePointerCapture(pointerIdRef.current);
      } catch { }
    }

    isDown.current = false;
    pointerIdRef.current = null;

    setTimeout(() => {
      moved.current = false;
    }, 0);

    scroller.style.cursor = "grab";
  };

  return (
    <section className="w-full">
      <div className="container-app py-12">

        {/* Header */}
        <div className="flex items-end justify-between gap-6">
          <h2 className="text-xl sm:text-3xl md:text-4xl font-semibold md:font-medium text-gray-900">
            {Heading}
          </h2>
        </div>

        {/* Carousel */}
        <div className="mt-7 relative">

          <div
            ref={scrollerRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={endDrag}
            onDragStart={(e) => e.preventDefault()}
            className={[
              "flex gap-6",
              "overflow-x-auto scroll-smooth",
              "snap-x snap-mandatory",
              "no-scrollbar",
              "cursor-grab",
              "select-none",
              "pr-[22%] sm:pr-[18%] lg:pr-[14%]",
            ].join(" ")}
          >
            {INSIGHTS.map((item, idx) => {
              const isActive = idx === active;
              

              return (
                <div
                  key={item.title}
                  ref={(el) => (slideRefs.current[idx] = el)}
                  className="snap-start shrink-0 w-[92%] sm:w-[86%] lg:w-[78%]"
                >
                  {isActive ? (
                    <div className="group relative block h-[340px] sm:h-[520px] overflow-hidden rounded-[28px]">

                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                      />

                      <div className="absolute inset-0 bg-black/20" />

                      <div className="absolute left-6 top-6">
                        <span className="inline-flex rounded-full border border-white/70 bg-black/30 px-4 py-2 text-[11px] font-semibold tracking-[0.12em] text-white">
                          {item.category}
                        </span>
                      </div>

                      <div className="absolute bottom-6 right-2 left-2 md:right-6 md:left-6 sm:left-auto sm:w-[520px]">

                        <div className="rounded-2xl bg-[#C9D0D6] px-2 md:px-6 py-5 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">

                          <div className="text-[11px] font-bold tracking-[0.14em] text-gray-800/80">
                            {item.type}
                            <span className="ml-2 font-medium tracking-[0.12em] text-gray-800/70">
                              {item.date}
                            </span>
                          </div>

                          <h3 className="mt-2 text-2xl font-medium text-gray-900">
                            {item.title}
                          </h3>

                          <p className="mt-3 text-sm text-gray-800/90 hidden sm:block">
                            {item.excerpt}
                          </p>

                          <div className="mt-3 flex justify-end sm:mt-5">

                            {/* FIXED BUTTON */}
                            <button
                               type="button"
                               onClick={() => {
                                 if (moved.current) return;
                                 console.log("Button clicked");
                                 if (onOpenDetail) {
                                   onOpenDetail(item);
                                 } else {
                                   window.location.href = item.to;
                                 }
                               }}
                              className="group/btn btn btn-bg text-gray-900"
                            >
                              Mehr erfahren
                              <ArrowRight className="h-5 w-6 transition-transform group-hover/btn:translate-x-0.5" />
                            </button>

                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        if (!moved.current) goTo(idx);
                      }}
                      className="group relative block w-full h-[340px] sm:h-[520px] overflow-hidden rounded-[28px] text-left"
                    >
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                      <div className="absolute inset-0 bg-black/35" />

                      <div className="absolute left-6 top-6">
                        <span className="inline-flex rounded-full border border-white/70 bg-black/30 px-4 py-2 text-[11px] font-semibold tracking-[0.12em] text-white">
                          {item.category}
                        </span>
                      </div>
                    </button>
                  )}
                </div>
              );
            })}

            {!onOpenDetail && (
              <button
                type="button"
                onClick={goNext}
                className="hidden lg:block absolute right-0 top-0 h-full w-[12%] pointer-events-none"
              />
            )}
          </div>

          <div className="mt-4 flex justify-end gap-3">
            {INSIGHTS.map((_, i) => (
              <Dot
                key={i}
                on={i === active}
                onClick={() => goTo(i)}
                label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display:none }
        .no-scrollbar { scrollbar-width:none; -ms-overflow-style:none }
      `}</style>
    </section>
  );
}